import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star, Loader2, Check, X, HelpCircle } from "lucide-react";

// Super-admin bulk-grant for the Beta Tester badge + default 0% fee override (6-month review
// clock). Paste a list of emails (idempotent, case-insensitive) and Run — the endpoint reports
// exactly which accounts were granted, which already had an override, and which don't exist yet
// (so you can retry as they register). This is ENVIRONMENT-SCOPED: run it on the environment whose
// database you're targeting (production accounts only exist in the production DB). Backed by
// POST /api/admin/beta-testers/grant.
export function BetaTesterBulkGrant({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [emails, setEmails] = useState("");
  const [rate, setRate] = useState("0");
  const [months, setMonths] = useState("6");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // {granted, already_had_override, not_found}

  const parsed = emails
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);

  const run = async () => {
    if (parsed.length === 0) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/beta-testers/grant", {
        emails: parsed,
        rate: Math.max(0, Math.min(100, parseFloat(rate) || 0)),
        months: Math.max(1, Math.min(60, parseInt(months, 10) || 6)),
      });
      setResult(data);
      const g = (data.granted || []).length;
      toast.success(`Granted ${g} · already ${(data.already_had_override || []).length} · not found ${(data.not_found || []).length}`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setRunning(false);
    }
  };

  const close = () => { if (!running) { setOpen(false); setResult(null); } };

  return (
    <div className="inline-flex" data-testid="beta-bulk-grant-panel">
      <button
        onClick={() => { setOpen(true); setResult(null); }}
        data-testid="beta-bulk-grant-open"
        className="inline-flex items-center gap-1.5 border border-volt/50 bg-volt/5 hover:bg-volt/10 text-volt font-mono uppercase text-[10px] px-3 py-1.5 transition-colors">
        <Star className="w-3.5 h-3.5" /> Bulk grant beta testers
      </button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-lg bg-surface border-sniper" data-testid="beta-bulk-grant-modal">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-foreground flex items-center gap-2">
              <Star className="w-5 h-5 text-volt" /> Bulk grant beta testers
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <p className="font-mono text-[10px] text-muted-foreground leading-relaxed">
              Grants the Beta Tester badge + a fee override (default 0% for 6 months, then admin-reviewed).
              Idempotent &amp; case-insensitive. Runs against THIS environment's database — production accounts
              only exist in production, so run it on the live site to reach real accounts.
            </p>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                Emails (one per line, or comma-separated)
              </label>
              <textarea
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                data-testid="beta-bulk-grant-emails"
                rows={5}
                placeholder={"person1@example.com\nperson2@example.com"}
                className="w-full mt-1 border border-sniper bg-inset text-foreground font-mono text-[12px] p-2 focus:border-volt outline-none" />
              <div className="text-right font-mono text-[10px] text-muted-foreground" data-testid="beta-bulk-grant-count">
                {parsed.length} email{parsed.length === 1 ? "" : "s"}
              </div>
            </div>

            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="block font-mono text-[9px] uppercase text-muted-foreground mb-1">Fee rate %</label>
                <input type="number" min={0} max={100} step="0.5" value={rate} onChange={(e) => setRate(e.target.value)}
                  data-testid="beta-bulk-grant-rate"
                  className="bg-inset border border-sniper focus:border-volt outline-none px-2 py-1.5 text-foreground font-mono text-xs w-20" />
              </div>
              <div>
                <label className="block font-mono text-[9px] uppercase text-muted-foreground mb-1">Review in (months)</label>
                <input type="number" min={1} max={60} value={months} onChange={(e) => setMonths(e.target.value)}
                  data-testid="beta-bulk-grant-months"
                  className="bg-inset border border-sniper focus:border-volt outline-none px-2 py-1.5 text-foreground font-mono text-xs w-20" />
              </div>
              <button onClick={run} disabled={running || parsed.length === 0}
                data-testid="beta-bulk-grant-run"
                className="inline-flex items-center gap-1.5 bg-volt text-black hover:bg-volt/90 font-mono uppercase text-[11px] px-4 py-2 transition-colors disabled:opacity-40 ml-auto">
                {running && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Run grant
              </button>
            </div>

            {result && (
              <div className="space-y-2 pt-1" data-testid="beta-bulk-grant-result">
                <ResultBlock icon={Check} tone="volt" label="Granted" testid="grant-granted"
                  rows={(result.granted || []).map((r) => `${r.email}${r.username ? ` (@${r.username})` : ""}`)} />
                <ResultBlock icon={HelpCircle} tone="muted" label="Already had an override" testid="grant-already"
                  rows={(result.already_had_override || []).map((r) => `${r.email}${r.username ? ` (@${r.username})` : ""}`)} />
                <ResultBlock icon={X} tone="blaze" label="Not found in this database" testid="grant-notfound"
                  rows={result.not_found || []} />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={close} data-testid="beta-bulk-grant-close"
                className="border border-sniper hover:border-foreground text-secondary-foreground font-mono uppercase text-[11px] px-4 py-2 transition-colors">
                Close
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResultBlock({ icon: Icon, tone, label, rows, testid }) {
  const toneCls = tone === "volt" ? "text-volt border-volt/30"
    : tone === "blaze" ? "text-blaze border-blaze/40"
    : "text-secondary-foreground border-sniper";
  return (
    <div data-testid={`beta-bulk-${testid}`}>
      <div className={`font-mono text-[10px] uppercase tracking-wide flex items-center gap-1.5 mb-1 ${tone === "volt" ? "text-volt" : tone === "blaze" ? "text-blaze" : "text-muted-foreground"}`}>
        <Icon className="w-3.5 h-3.5" /> {label} ({rows.length})
      </div>
      {rows.length > 0 && (
        <div className="border border-sniper bg-inset max-h-28 overflow-y-auto p-2 flex flex-wrap gap-1">
          {rows.map((r, i) => (
            <span key={i} className={`font-mono text-[10px] px-1.5 py-0.5 border normal-case ${toneCls}`}>{r}</span>
          ))}
        </div>
      )}
    </div>
  );
}
